import { motion } from 'framer-motion'

import Image from 'next/image'

import IconCap from '@assets/icon_index_cap.svg'
import IconPanel from '@assets/icon_index_panel.svg'
import IconComputer from '@assets/computer.svg'
import IconPeople from '@assets/icon_index_people.svg'

import ABFLogoImg from '@assets/abf-logo.webp'
import certificacaoAbsolarImg from '@assets/certificacao-absolar.webp'
import pvLogo from '@assets/pv-operation-logo.png'
import GoogleImage from '@assets/google-logo.svg'
import melhoresFranquiasImg from '@assets/melhores-franquias.webp'
import { HomeLinkButton } from '../components/home-link-button'

type Props = {
  showFranchiseButton?: boolean
}

export const LeaderBrandSection = ({ showFranchiseButton = true }: Props) => {
  return (
    <section
      id="leader-brand"
      className="w-full md:h-[100svh] flex md:snap-always md:snap-start justify-center md:overflow-hidden pt-16 px-4 md:px-8"
    >
      <div className="w-full xl:max-w-7xl flex items-center justify-center">
        <div className="w-full h-full flex flex-col lg:grid lg:grid-cols-2 gap-4 md:gap-8 lg:gap-2 lg:relative pb-12">
          <motion.div
            whileInView={{
              opacity: 1,
              x: 0
            }}
            variants={{
              hidden: {
                opacity: 0,
                x: -30
              }
            }}
            initial="hidden"
            transition={{
              duration: 0.5,
              delay: 0.5
            }}
            viewport={{
              once: true
            }}
            className="w-full h-full flex flex-col justify-center items-center lg:items-start order-2 lg:order-1"
          >
            <div className="mb-8 mt-2 md:mt-0 md:mb-12 flex justify-center lg:justify-start">
              <Image
                src={pvLogo}
                className="w-auto h-8 md:h-[66px]"
                alt="PV Operation"
              />
            </div>
            <h2 className="w-full text-2xl md:text-[35px] leading-[30px] lg:text-[30px] lg:leading-[35px] 2xl:text-[35px] tracking-[-2.5%] text-v3-gray-600 mb-10 md:mb-10 lg:mb-10 text-center lg:text-left lg:relative lg:top-0 lg:w-full lg:px-0">
              Operação e Monitoramento Remoto <br />
              de Usinas de Energia Solar
            </h2>

            <ul className="text-sm leading-[14px] md:text-base md:leading-[19px] xl:text-lg xl:leading-[22px] text-black space-y-2.5 md:space-y-4 2xl:space-y-6 w-full flex flex-col items-center lg:items-start">
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                + de <strong>5GW</strong> monitorados
              </li>
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                + de <strong>60.000</strong> usinas solares cadastradas
              </li>
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                <strong>Monitoramento e Operação Remota</strong> de usinas
                solares
              </li>
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                Sistema Supervisório <strong>(Scade PV)</strong>
              </li>
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                Smart Loggers e Smart Meters
              </li>
              <li className="before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-v3-gray-425 before:inline-block before:mr-2">
                Suporte remoto especializado
              </li>
            </ul>

            <div className="w-full flex justify-center lg:justify-start">
              <HomeLinkButton
                href="https://pvoperation.com"
                rel="nofollow"
                className="max-w-[20.875rem] mt-14 md:mt-6 xl:mt-5 2xl:mt-10 1.5xl:mt-11"
                target="_blank"
              >
                SAIBA MAIS
              </HomeLinkButton>
            </div>
          </motion.div>
          <motion.div
            whileInView={{
              opacity: 1,
              x: 0
            }}
            variants={{
              hidden: {
                opacity: 0,
                x: 30
              }
            }}
            initial="hidden"
            transition={{
              duration: 0.5,
              delay: 0.5
            }}
            viewport={{
              once: true
            }}
            className="w-full h-full relative flex justify-end order-1 lg:order-2"
          >
            <div className="relative w-full h-full rounded-lg overflow-hidden lg:max-w-[480px] 2xl:max-w-[640px] min-h-[200px]">
              <Image
                src="/assets/centro_de_monitoramento_e_operacao_de_usinas_solare.jpg"
                alt="Centro de monitoramento e operações de usinas solares PV Operation"
                fill
                width={0}
                height={0}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover object-[50%_60%] lg:object-center"
                loading="lazy"
              />
              <div className="w-full absolute bottom-10 hidden lg:flex justify-center text-white gap-[26px]">
                <div className="w-16 lg:w-28 flex flex-col items-center [&>svg]:h-[22px] [&>svg]:xl:h-9">
                  <IconPanel />
                  <p className="text-lg leading-3 lg:text-3xl lg:leading-6 tracking-[-0.8px] font-bold mt-2 xl:mt-3">
                    60.000
                  </p>
                  <span className="text-[10px] leading-[8px] lg:text-base lg:leading-[14px] tracking-[-0.2px] text-center mt-1">
                    usinas solares
                  </span>
                </div>

                <div className="w-16 lg:w-28 flex flex-col items-center [&>svg]:h-[22px] [&>svg]:xl:h-9">
                  <IconCap />
                  <p className="text-lg leading-3 lg:text-3xl lg:leading-6 tracking-[-0.8px] font-bold mt-2 xl:mt-3">
                    500
                  </p>
                  <span className="text-[10px] leading-[8px] lg:text-base lg:leading-[14px] tracking-[-0.2px] text-center mt-1">
                    instaladores parceiros
                  </span>
                </div>

                <div className="w-16 lg:w-28 flex flex-col items-center [&>svg]:h-[22px] [&>svg]:xl:h-9">
                  <IconComputer />
                  <p className="text-lg leading-3 lg:text-3xl lg:leading-6 tracking-[-0.8px] font-bold mt-2 xl:mt-3">
                    +5GW
                  </p>
                  <span className="text-[10px] leading-[8px] lg:text-base lg:leading-[14px] tracking-[-0.2px] text-center mt-1">
                    monitorados
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
